import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { PencilSquareIcon, PhotoIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Alert, Button, Card, LoadingState } from './ui';
import { useAuth } from '../context/AuthContext';
import accountProfileService from '../services/accountProfileService';
import AccountTwoFactorSettings from './AccountTwoFactorSettings';
import { prepareTelegramAvatar, profileAvatar, profileErrorMessage } from '../utils/telegramProfile';

const inputClass = 'mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500';
const emptyDraft = { first_name: '', last_name: '', bio: '' };

export default function AccountTelegramProfile({ account, authInProgress = false, onSaved }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [avatar, setAvatar] = useState(null);
  const [avatarChanged, setAvatarChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState('');
  const [partialUpdate, setPartialUpdate] = useState(false);
  const [success, setSuccess] = useState('');
  const [reload, setReload] = useState(0);
  const mounted = useRef(true);
  const savingRef = useRef(false);
  const imageVersion = useRef(0);
  const profileVersion = useRef(0);
  const editorOpen = useRef(false);
  const loadedRequest = useRef(null);
  const fileInput = useRef(null);
  const canManage = ['admin', 'manager'].includes(user?.role);
  const connected = account.status === 'on' && account.has_session === true && account.live_status !== 'offline' && !authInProgress;
  const editable = canManage && connected;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; imageVersion.current += 1; };
  }, []);

  useEffect(() => {
    if (!editable) { setLoading(false); return undefined; }
    // Reconnection may refresh the summary, but must not replace an open draft.
    if (editorOpen.current && loadedRequest.current?.accountId === account.id && loadedRequest.current.reload === reload) return undefined;
    let active = true;
    const version = ++profileVersion.current;
    setLoading(true);
    setError('');
    setPartialUpdate(false);
    accountProfileService.get(account.id).then(data => {
      if (!active || version !== profileVersion.current) return;
      loadedRequest.current = { accountId: account.id, reload };
      setProfile(data);
      setDraft({ first_name: data.first_name || '', last_name: data.last_name || '', bio: data.bio || '' });
      setAvatar(profileAvatar(data.avatar_url));
      setAvatarChanged(false);
      setEditorReady(true);
    }).catch(loadError => {
      if (active && version === profileVersion.current) setError(profileErrorMessage(loadError));
    }).finally(() => { if (active && version === profileVersion.current) setLoading(false); });
    return () => { active = false; };
  }, [account.id, editable, reload]);

  const close = () => {
    if (savingRef.current) return;
    profileVersion.current += 1;
    setLoading(false);
    imageVersion.current += 1;
    setPreparing(false);
    editorOpen.current = false;
    setEditorReady(false);
    setOpen(false);
    setAvatar(null);
    setDraft(emptyDraft);
    if (!profile) setReload(value => value + 1);
    if (fileInput.current) fileInput.current.value = '';
  };

  const chooseAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file || savingRef.current) return;
    const version = ++imageVersion.current;
    setPreparing(true);
    setError('');
    try {
      const image = await prepareTelegramAvatar(file);
      if (mounted.current && version === imageVersion.current) { setAvatar(image); setAvatarChanged(true); }
    } catch (imageError) {
      if (mounted.current && version === imageVersion.current) setError(imageError.message);
    } finally {
      if (mounted.current && version === imageVersion.current) {
        setPreparing(false);
        if (fileInput.current) fileInput.current.value = '';
      }
    }
  };

  const changes = {};
  if (profile) Object.entries(draft).forEach(([key, value]) => {
    const original = String(profile[key] || '');
    if (value !== original && value.trim() !== original) changes[key] = value.trim();
  });
  if (avatarChanged) changes.avatar_url = avatar;
  const hasChanges = Object.keys(changes).length > 0;
  const bioLimit = Number.isSafeInteger(profile?.bio_max_length) && profile.bio_max_length > 0
    ? Math.min(255, profile.bio_max_length) : 255;

  const save = async (event) => {
    event.preventDefault();
    if (savingRef.current || preparing || loading || !editable || !editorReady || !profile || !hasChanges || partialUpdate) return;
    if (!draft.first_name.trim()) { setError('Введите имя в Telegram.'); return; }
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      const updated = await accountProfileService.update(account.id, changes);
      if (mounted.current) {
        setProfile(updated);
        setSuccess('Профиль сохранён в Telegram.');
        editorOpen.current = false;
        setOpen(false);
        setDraft(emptyDraft);
        setAvatar(null);
        setAvatarChanged(false);
      }
      await onSaved?.();
    } catch (saveError) {
      if (mounted.current) {
        setError(profileErrorMessage(saveError));
        setPartialUpdate(saveError?.response?.data?.partial_update === true);
      }
    } finally {
      savingRef.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const fullName = profile ? [profile.first_name, profile.last_name].filter(Boolean).join(' ') : '';
  const summaryAvatar = profileAvatar(profile?.avatar_url);
  const draftName = [draft.first_name, draft.last_name].filter(Boolean).join(' ');
  const hasPhoto = avatarChanged ? Boolean(avatar) : profile?.has_photo === true;

  return (
    <Card title="Профиль Telegram" className="mb-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          {summaryAvatar && <img src={summaryAvatar} alt="Текущее фото профиля Telegram" className="h-14 w-14 rounded-full object-cover" />}
          <div className="min-w-0">
            {fullName && <p className="break-words font-medium text-gray-900">{fullName}</p>}
            {profile?.username && <p className="break-all text-sm text-gray-500">@{profile.username}</p>}
            {profile?.bio && <p className="whitespace-pre-wrap break-words text-sm text-gray-700">{profile.bio}</p>}
            <p className="text-sm text-gray-500">Имя, фамилия, описание и фото, которые видят пользователи Telegram.</p>
          </div>
        </div>
        <Button variant="secondary" icon={PencilSquareIcon} disabled={!editable} onClick={() => { editorOpen.current = true; setEditorReady(false); setSuccess(''); setLoading(true); setReload(value => value + 1); setOpen(true); }}>Изменить профиль Telegram</Button>
      </div>
      {!open && loading && <LoadingState message="Загружаем актуальный профиль из Telegram…" />}
      {!open && error && editable && <div className="mt-3" role="alert"><Alert variant="error">{error}</Alert><Button variant="secondary" className="mt-2" disabled={loading} onClick={() => setReload(value => value + 1)}>Загрузить профиль заново</Button></div>}
      {!canManage && <p className="mt-3 text-sm text-gray-500">Редактирование доступно администратору и руководителю.</p>}
      {canManage && !connected && <p className="mt-3 text-sm text-gray-500">Для редактирования включите аккаунт и завершите подключение Telegram.</p>}
      {success && <Alert variant="success" className="mt-4">{success}</Alert>}

      <AccountTwoFactorSettings key={account.id} account={account} authInProgress={authInProgress} />

      <Modal title="Профиль Telegram" open={open} onCancel={close} footer={null} destroyOnClose
        closable={!saving} maskClosable={!saving} keyboard={!saving} width={560} styles={{ body: { maxHeight: '70dvh', overflowY: 'auto' } }}>
        <p className="mb-4 text-sm text-gray-500">Изменения будут видны в Telegram после нажатия «Сохранить в Telegram».</p>
        {loading && <LoadingState message="Загружаем актуальный профиль из Telegram…" />}
        {error && <div role="alert" className="mb-4"><Alert variant="error">{error}</Alert></div>}
        {partialUpdate && <Alert variant="warning" className="mb-4">Часть изменений могла примениться. Загрузите профиль заново, прежде чем сохранять ещё раз.</Alert>}
        {!loading && profile && editorReady && <form onSubmit={save} className="space-y-4">
          <fieldset disabled={saving || preparing || !editable || partialUpdate} className="space-y-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              {avatar ? <img src={avatar} alt="Фото профиля Telegram" className="h-20 w-20 rounded-full object-cover" /> : <span className="flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 text-2xl text-gray-500" aria-label={hasPhoto ? 'Предпросмотр фото недоступен' : 'Фото не установлено'}>{draftName.trim().slice(0, 1) || '?'}</span>}
              <div className="min-w-0 space-y-2">
                <label className="block text-sm font-medium"><span className="flex items-center gap-1"><PhotoIcon className="h-4 w-4" />Выбрать фото</span><input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" onChange={chooseAvatar} className="mt-1 block w-full min-w-0 text-sm" /></label>
                {hasPhoto && <Button variant="secondary" size="sm" icon={TrashIcon} onClick={() => { imageVersion.current += 1; setAvatar(null); setAvatarChanged(true); }}>Удалить текущее фото</Button>}
                <p className="text-xs text-gray-500">JPG, PNG или WebP до 5 МБ. Фото будет подготовлено автоматически.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block text-sm font-medium text-gray-700">Имя<input autoComplete="off" required maxLength={64} value={draft.first_name} onChange={event => setDraft(current => ({ ...current, first_name: event.target.value }))} className={inputClass} /></label>
              <label className="block text-sm font-medium text-gray-700">Фамилия<input autoComplete="off" maxLength={64} value={draft.last_name} onChange={event => setDraft(current => ({ ...current, last_name: event.target.value }))} className={inputClass} /></label>
            </div>
            <label className="block text-sm font-medium text-gray-700">О себе<input maxLength={bioLimit} value={draft.bio} onChange={event => setDraft(current => ({ ...current, bio: event.target.value.replace(/[\r\n]/g, ' ') }))} className={inputClass} placeholder="Описание профиля в Telegram" /><span className="mt-1 block text-xs font-normal text-gray-500">{draft.bio.length} / {bioLimit}</span></label>
            {profile.username && <p className="text-sm text-gray-500">Username: @{profile.username} · только просмотр</p>}
          </fieldset>
          {preparing && <p role="status" className="text-sm text-gray-500">Подготавливаем фото…</p>}
          <div className="flex flex-wrap justify-end gap-2"><Button variant="secondary" disabled={saving} onClick={close}>Отмена</Button><Button type="submit" isLoading={saving} disabled={preparing || !editable || !draft.first_name.trim() || !hasChanges || partialUpdate}>Сохранить в Telegram</Button></div>
        </form>}
        {!loading && error && <Button variant="secondary" className="mt-4" disabled={saving || preparing} onClick={() => { setProfile(null); setReload(value => value + 1); }}>Загрузить профиль заново</Button>}
      </Modal>
    </Card>
  );
}
